import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { ReplenishmentThresholdVm } from 'api/replenishment/replenishment-threshold.vm';
import { LocationSelectionDialog } from '../../components/LocationSelectionDialog';
import ProductSelectionDialog from '../../components/ProductSelectionDialog';
import { LocationVm } from 'api/warehouse/location.vm';
import React, { useEffect, useState } from 'react';
import { ProductVm } from 'api/catalog/product.vm';

interface SetReplenishmentThresholdDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (warehouseId: string, locationId: string, productId: string, min: number, max: number) => void;
  warehouseId: string;
  editThreshold?: ReplenishmentThresholdVm | null;
}

const SetReplenishmentThresholdDialog = ({ open, onClose, onSave, warehouseId, editThreshold }: SetReplenishmentThresholdDialogProps) => {
  const [location, setLocation] = useState<LocationVm | null>(null);
  const [product, setProduct] = useState<ProductVm | null>(null);
  const [minQty, setMinQty] = useState<number>(0);
  const [maxQty, setMaxQty] = useState<number>(0);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // Validation logic
  const isMaxValid = maxQty > 0 && maxQty >= minQty;

  useEffect(() => {
    if (open) {
      if (editThreshold) {
        setLocation({ id: editThreshold.locationId } as LocationVm);
        setProduct({ id: editThreshold.productId, sku: editThreshold.productSku } as ProductVm);
        setMinQty(editThreshold.min);
        setMaxQty(editThreshold.max);
      } else {
        setLocation(null);
        setProduct(null);
        setMinQty(0);
        setMaxQty(0);
      }
    }
  }, [open, editThreshold]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Definir Nível de Reposição</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Armazém"
                  fullWidth
                  value={warehouseId || ''}
                  InputProps={{ readOnly: true }}
                  placeholder="Armazém"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Localização"
                  fullWidth
                  value={location ? location.id : ''}
                  onClick={() => !editThreshold && warehouseId && setLocationDialogOpen(true)}
                  InputProps={{ readOnly: true }}
                  placeholder="Selecionar Localização"
                  required
                  disabled={!!editThreshold}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Produto"
                  fullWidth
                  value={product ? product.sku : ''}
                  onClick={() => !editThreshold && setProductDialogOpen(true)}
                  InputProps={{ readOnly: true }}
                  placeholder="Selecionar Produto"
                  required
                  disabled={!!editThreshold}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mínimo"
                  type="number"
                  fullWidth
                  value={minQty}
                  onChange={(e) => setMinQty(parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Máximo"
                  type="number"
                  fullWidth
                  value={maxQty}
                  onChange={(e) => setMaxQty(parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => onSave(warehouseId, location!.id, product!.id, minQty, maxQty)}
            disabled={!warehouseId || !location || !product || !minQty || !maxQty || !isMaxValid}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <LocationSelectionDialog
        open={locationDialogOpen}
        warehouseId={warehouseId}
        onClose={() => setLocationDialogOpen(false)}
        onLocationSelect={(loc) => {
          setLocation(loc);
          setLocationDialogOpen(false);
        }}
      />
      <ProductSelectionDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        onProductSelect={(prod) => {
          setProduct(prod);
          setProductDialogOpen(false);
        }}
      />
    </>
  );
};

export default SetReplenishmentThresholdDialog;
